import { useState } from "react";
import { api, getApiError } from "@/lib/api";
import { fmtLog } from "@/lib/tz";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2, Circle, Loader2, Send, Undo2, ThumbsUp, MessageSquareWarning, ShieldCheck, Users } from "lucide-react";
import { DOCS_ACCENT as ACCENT, fmtDay, nextVersion } from "@/lib/govDocs";

const NO_MEETING = "__none__";

/**
 * Management Team approval for a draft: submit, approve / request changes, withdraw.
 * The final approval makes the draft Active automatically (onPublished); Make Active is only a
 * fallback if that didn't happen. `approval` comes from GET /governance/documents/:id.
 */
export default function DraftApprovalPanel({ docId, approval, latestVersion, importVersion, beforeSubmit, onChanged, onPublished, onMakeActive, disabled }) {
  const [dlg, setDlg] = useState(null);          // "submit" | "approve" | "changes" | "withdraw"
  const [text, setText] = useState("");
  const [bump, setBump] = useState("minor");     // minor (1.3 -> 1.4) or major (2.0), applied when it goes Active
  const [meeting, setMeeting] = useState(NO_MEETING);
  const [meetings, setMeetings] = useState([]);
  const [busy, setBusy] = useState(false);
  const pending = approval.state === "pending";

  const open = async (kind) => {
    setText(""); setMeeting(NO_MEETING); setBump("minor"); setDlg(kind);
    if (kind === "approve") api.govMeetings().then((r) => setMeetings(r.meetings.slice(0, 20))).catch(() => setMeetings([]));
  };

  const act = async () => {
    setBusy(true);
    try {
      if (dlg === "submit") {
        if (!(await beforeSubmit())) { toast.error("Couldn't save the draft, so it wasn't submitted."); return; }
        const r = await api.govSubmitDraft(docId, text, bump);
        toast.success(r.notified ? `Submitted. ${r.notified} team member${r.notified === 1 ? " has" : "s have"} been emailed.` : "Submitted for approval.");
      } else if (dlg === "approve") {
        const r = await api.govApproveDraft(docId, { comment: text, meeting_id: meeting === NO_MEETING ? null : meeting });
        if (r.published) {
          const p = r.published;
          toast.success(`Approved by everyone. Version ${p.version} is now Active${p.archived_version ? ` and version ${p.archived_version} was archived` : ""}.`);
          setDlg(null);
          await onPublished(p);
          return;
        }
        toast.success(r.complete ? "Approved by everyone." : `Approved (${r.approved} of ${r.required}).`);
      } else if (dlg === "changes") {
        await api.govRequestChanges(docId, text);
        toast.success("Changes requested. The draft is back in editing.");
      } else if (dlg === "withdraw") {
        await api.govWithdrawDraft(docId);
        toast.success("Withdrawn. You can edit the draft again.");
      }
      setDlg(null);
      await onChanged();
    } catch (e) {
      toast.error(getApiError(e, "Something went wrong."));
    } finally { setBusy(false); }
  };

  const lastChanges = approval.last_changes_requested;

  return (
    <div className="rounded-xl border bg-white mb-3 overflow-hidden" style={{ borderColor: pending ? `${ACCENT}55` : "#e2e8f0" }} data-testid="approval-panel">
      {!pending ? (
        <div className="px-4 py-3">
          {lastChanges && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 mb-3 text-xs text-amber-900" data-testid="changes-requested">
              <MessageSquareWarning className="h-4 w-4 shrink-0 mt-0.5" />
              <div><span className="font-semibold">{lastChanges.by_name} requested changes</span> on {fmtLog(lastChanges.at)}:
                <div className="whitespace-pre-line mt-0.5">{lastChanges.comment}</div></div>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-slate-600 flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 shrink-0" style={{ color: ACCENT }} />
              <span>When the draft is ready, submit it for approval. Every Management Team member
                ({approval.members.length ? approval.members.map((m) => m.full_name).join(", ") : "no members yet"}) must approve it.
                Once everyone has, it becomes the Active version automatically. The text is locked while it's being approved.</span>
            </div>
            <Button size="sm" className="h-8 text-xs shrink-0" style={{ backgroundColor: ACCENT }} disabled={disabled || !approval.members.length}
              onClick={() => open("submit")} data-testid="approval-submit">
              <Send className="h-3.5 w-3.5 mr-1" /> Submit for approval
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-b border-slate-100" style={{ backgroundColor: `${ACCENT}0d` }}>
            <div className="text-sm">
              <div className="font-semibold text-slate-800" data-testid="approval-progress">
                {approval.complete ? "Approved by everyone: ready to make Active" : `Awaiting approval: ${approval.approved} of ${approval.members.length} approved`}
              </div>
              <div className="text-[11px] text-slate-500">
                Submitted by {approval.submitted_by} on {fmtLog(approval.submitted_at)}. The text is locked until it's made Active or withdrawn.
                {!approval.complete && <> It becomes version {nextVersion(latestVersion, approval.bump, importVersion)} and goes Active automatically once everyone approves.</>}
              </div>
              {approval.note && <div className="text-xs text-slate-600 mt-1">Note: {approval.note}</div>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="outline" className="h-8 text-xs" disabled={disabled} onClick={() => open("withdraw")} data-testid="approval-withdraw">
                <Undo2 className="h-3.5 w-3.5 mr-1" /> Withdraw to edit
              </Button>
              {approval.can_approve && (
                <>
                  <Button size="sm" variant="outline" className="h-8 text-xs" disabled={disabled} onClick={() => open("changes")} data-testid="approval-changes">
                    <MessageSquareWarning className="h-3.5 w-3.5 mr-1" /> Request changes
                  </Button>
                  <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" disabled={disabled} onClick={() => open("approve")} data-testid="approval-approve">
                    <ThumbsUp className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                </>
              )}
              {approval.complete && (
                <Button size="sm" className="h-8 text-xs" style={{ backgroundColor: ACCENT }} disabled={disabled} onClick={onMakeActive} data-testid="draft-publish">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Make Active
                </Button>
              )}
            </div>
          </div>
          {approval.approvals_reset && (
            <div className="flex items-start gap-2 bg-amber-50 border-b border-amber-200 px-4 py-2 text-xs text-amber-900" data-testid="approvals-reset">
              <Users className="h-4 w-4 shrink-0" />
              <span>Approvals were cleared on {fmtLog(approval.approvals_reset.at)} because {approval.approvals_reset.comment.replace(/\.$/, "")}. Everyone on the team now needs to approve it again.</span>
            </div>
          )}
          <div className="divide-y divide-slate-100" data-testid="approval-members">
            {approval.members.map((m) => (
              <div key={m.username} className="flex items-start gap-2.5 px-4 py-2">
                {m.status === "approved" ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> : <Circle className="h-4 w-4 text-slate-300 mt-0.5" />}
                <div className="min-w-0 text-sm">
                  <span className="font-medium text-slate-700">{m.full_name}</span>
                  <span className="text-[11px] text-slate-500 ml-2">
                    {m.status === "approved" ? `Approved ${fmtLog(m.at)}` : "Waiting"}
                    {m.meeting && <> · at {m.meeting.title} ({fmtDay(m.meeting.date)})</>}
                  </span>
                  {m.comment && <div className="text-xs text-slate-600">{m.comment}</div>}
                </div>
              </div>
            ))}
            {!approval.on_team && approval.members.length > 0 && (
              <div className="px-4 py-2 text-[11px] text-slate-400 flex items-center gap-1.5"><Users className="h-3 w-3" />Only Management Team members can approve.</div>
            )}
          </div>
        </>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-brand text-base">
              {dlg === "submit" && "Submit for approval"}
              {dlg === "approve" && "Approve this draft"}
              {dlg === "changes" && "Request changes"}
              {dlg === "withdraw" && "Withdraw from approval?"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {dlg === "submit" && `The draft is saved and locked, and every Management Team member is emailed to approve it. Once everyone has approved, it becomes the Active version automatically${latestVersion ? " and the current version is archived" : ""}.`}
              {dlg === "approve" && (approval.approved + 1 >= approval.members.length
                ? "Your approval is recorded against the exact text submitted. You're the last to approve, so this version becomes Active straight away."
                : "Your approval is recorded against the exact text submitted.")}
              {dlg === "changes" && "The draft goes back into editing and all approvals so far are cleared. The person who submitted it is emailed."}
              {dlg === "withdraw" && `The draft goes back into editing so it can be changed.${approval.approved ? ` The ${approval.approved} approval${approval.approved === 1 ? "" : "s"} so far will be cleared.` : ""}`}
            </DialogDescription>
          </DialogHeader>
          {dlg && dlg !== "withdraw" && (
            <div className="space-y-3">
              {dlg === "submit" && latestVersion && (
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Type of update</Label>
                  <div className="flex gap-2" data-testid="submit-bump">
                    {[["minor", "Minor update", "Small changes and corrections"], ["major", "Major update", "A rewrite or significant change"]].map(([k, l, d]) => (
                      <button key={k} type="button" onClick={() => setBump(k)} data-testid={`submit-bump-${k}`}
                        className={`flex-1 text-left rounded-lg border px-3 py-2 ${bump === k ? "border-[#6b4f8a] bg-[#6b4f8a0f]" : "border-slate-200 hover:bg-slate-50"}`}>
                        <div className="text-sm font-medium text-slate-800">{l}: {nextVersion(latestVersion, k)}</div>
                        <div className="text-[11px] text-slate-500">{d}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {dlg === "submit" && !latestVersion && (
                <p className="text-xs text-slate-500">This will become version {nextVersion(null, "minor", importVersion)}.</p>
              )}
              <div className="space-y-1">
                <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {dlg === "changes" ? "What needs to change?" : dlg === "submit" ? "Note for the team (optional)" : "Comment (optional)"}
                </Label>
                <Textarea value={text} rows={3} className="text-sm" onChange={(e) => setText(e.target.value)} data-testid="approval-text" />
              </div>
              {dlg === "approve" && (
                <div className="space-y-1">
                  <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Approved at a meeting? (optional)</Label>
                  <Select value={meeting} onValueChange={setMeeting}>
                    <SelectTrigger className="h-9 text-sm bg-white" data-testid="approval-meeting"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_MEETING} className="text-sm">No meeting</SelectItem>
                      {meetings.map((m) => <SelectItem key={m.id} value={m.id} className="text-sm">{fmtDay(m.date)} · {m.title}{m.joint ? " (joint)" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDlg(null)}>Cancel</Button>
            <Button size="sm" onClick={act} disabled={busy || (dlg === "changes" && !text.trim())} data-testid="approval-confirm"
              className={dlg === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : ""} style={dlg === "approve" ? undefined : { backgroundColor: ACCENT }}>
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {dlg === "submit" ? "Submit" : dlg === "approve" ? "Approve" : dlg === "changes" ? "Send back for changes" : "Withdraw"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
